import { useNavigate, useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  usePostBroadcast,
  usePutBroadcast,
} from "@/api/mutations";
import {
  useGetBroadcast,
  useGetBroadcasts,
  useGetMyAccount,
  useGetMyProfile,
  useGetMySongs,
} from "@/api/queries";
import FormError from "@/components/FormError";
import H2 from "@/components/H2";
import InnerRadio from "@/components/InnerRadio";
import Input from "@/components/Input";
import LoadingSmall from "@/components/LoadingSmall";
import RebrandPrimaryButton from "@/components/RebrandPrimaryButton";
import Select from "@/components/Select";
import Spacer from "@/components/Spacer";
import TextArea from "@/components/TextArea";
import countries from "@/data/countries";
import { sendProperty } from "@/helpers/helpers";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

function BroadcastsCreateUpdate() {
  const { t } = useTranslation(["other", "countries"]);
  const { broadcastId } = useParams({ strict: false });

  const isUpdate = !!broadcastId;
  const profile = useGetMyProfile();
  const [medium, setMedium] = useState();
  const [station, setStation] = useState();
  const [program, setProgram] = useState();
  const [country, setCountry] = useState();
  const [dateAired, setDateAired] = useState();
  const [chosenSong, setChosenSong] = useState();
  const [evidence, setEvidence] = useState();

  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const createMutation = usePostBroadcast();
  const updateMutation = usePutBroadcast();

  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);

  const broadcast = useGetBroadcast({ ac: selectedAccount, id: broadcastId });
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });

  const broadcasts = useGetBroadcasts({
    ac: selectedAccount,
    oc: myAccount?.data?.originatorcode?.code,
  });

  const songs = useGetMySongs({ aac: selectedAccount });

  const isTunecore = useUIStateStore(state => state.isTunecore);

  useEffect(() => {
    if (selectedAccount) {
      songs.refetch();
      broadcast.refetch();
    }
  }, [selectedAccount, songs, broadcast]);

  if (broadcastId && broadcast.data?.length === 0) {
    navigate({ to: "/broadcasts" });
  }

  if (
    broadcast.isSuccess
    && broadcast.data
    && broadcast.data[0]
    && broadcastId
  ) {
    const dateNotValid = Number.isNaN(Date.parse(broadcast.data[0].date_aired));
    setDateAired(
      dateNotValid
        ? null
        : format(Date.parse(broadcast.data[0].date_aired), "yyyy-MM-dd"),
    );
    setEvidence(broadcast.data[0]?.evidence);
    setMedium(broadcast.data[0]?.medium);
    setCountry(broadcast.data[0]?.country);
    setProgram(broadcast.data[0]?.program);
    setChosenSong(
      JSON.parse(broadcast.data[0].song).workcode
      || JSON.parse(broadcast.data[0].song).tc,
    );
    setStation(broadcast.data[0].station);
  }

  const checkErrors = () => {
    const errs = {};

    let valid = true;
    // title check
    if (!chosenSong) {
      errs.chosenSong = t("regex:select-song-error");
      valid = false;
    }

    if (!dateAired) {
      errs.dateAired = t("regex:date-aired-error");
      valid = false;
    }

    function isValidDate(d) {
      return d instanceof Date && !Number.isNaN(d);
    }

    if (!isValidDate(new Date(dateAired))) {
      errs.dateAired = t("regex:invalid-date-error");
      valid = false;
    }

    if (new Date(dateAired) > new Date()) {
      errs.dateAired = t("regex:date-in-future-error");
      valid = false;
    }

    if (!country) {
      errs.country = t("regex:choose-country-error");
      valid = false;
    }

    if (!medium) {
      errs.medium = t("regex:choose-medium-error");
      valid = false;
    }

    if (!program || program.trim().length === 0) {
      errs.program = t("regex:add-program-error");
      valid = false;
    }

    if (!station || station.trim().length === 0) {
      errs.station = t("regex:add-station-error");
      valid = false;
    }

    if (!evidence || evidence.trim().length === 0) {
      errs.evidence = t("regex:add-evidence-error");
      valid = false;
    }

    setErrors(errs);
    return valid;
  };

  const addBroadcast = async () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    // reset mutation
    createMutation.reset();
    const valid = checkErrors();

    if (valid) {
      const data = {
        account_name: myAccount.data.artistaccountcode,
        legacy_name: myAccount?.data?.originatorcode?.code,
        song: JSON.stringify(
          songs.data.works.find(w => w.workcode === chosenSong),
        ),
        medium,
        country,
        station,
        program,
        date_aired: dateAired,
        evidence,
      };

      createMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.failedvalidation) {
            setValidationErrorMessage(response.data.messages);
            setPending(false);
          }
          else {
            toast.success(t("other:successfully-added-broadcast-toast"));
            broadcasts.refetch();
            broadcast.refetch();
            navigate({ to: "/broadcasts" });
            sendProperty({
              property: "broadcast_opp_applicant",
              value: true,
              email: profile.data.email,
            });
            setPending(false);
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    else {
      setPending(false);
    }
  };

  const updateBroadcast = async () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    // reset mutation
    updateMutation.reset();
    const valid = checkErrors();

    if (valid) {
      const data = {
        id: broadcastId,
        account_name: myAccount.data.artistaccountcode,
        legacy_name: myAccount.data?.originatorcode?.code,
        song: JSON.stringify(
          songs.data.works.find(w => w.workcode === chosenSong),
        ),
        medium,
        country,
        station,
        program,
        date_aired: dateAired,
        evidence,
      };

      updateMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.failedvalidation) {
            setValidationErrorMessage(response.data.messages);
            setPending(false);
          }
          else {
            toast.success(t("other:successfully-updated-broadcast-toast"));
            broadcasts.refetch();
            navigate({ to: "/broadcasts" });
            setPending(false);
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    else {
      setPending(false);
    }
  };

  return (
    <>
      <div className="flex-1 order-last xl:w-full bg-sentric-rebrand-light-grey min-h-50 lg:min-h-screen-minus-header">
        {songs.isSuccess && (
          <div>
            <div className="flex flex-wrap justify-between">
              <H2>
                {isUpdate
                  ? t("other:update-broadcast")
                  : t("other:add-broadcast")}
              </H2>
            </div>
            <Spacer />
            <div className="w-full lg:w-2/3">
              <Select
                id="txtBroadcastSelectSong"
                className="w-full text-old-sentric-dark-gray"
                name="country"
                placeholder={t("other:select-a-song-placeholder")}
                error={errors.chosenSong}
                value={chosenSong}
                onChange={(selected) => {
                  setChosenSong(selected);
                }}
                options={songs?.data?.works.map(s => ({
                  name: s.title,
                  value: s.workcode,
                }))}
                search
                required
              />
              <Spacer />
              <InnerRadio
                name="medium"
                label={t("other:choose-medium")}
                value={medium}
                onChange={e => setMedium(e.target.value)}
                options={[
                  { label: t("other:radio"), value: "Radio" },
                  { label: t("other:tv"), value: "TV" },
                ]}
                error={errors.medium}
                required
              />
              <Spacer />
              <Input
                id="txtBroadcastStation"
                required
                type="text"
                error={errors.station}
                placeholder={t("other:station-channel-placeholder")}
                value={station}
                onBlur={() => {}}
                onChange={(e) => {
                  setStation(e.target.value);
                }}
              />
              <Spacer />
              <Input
                id="txtBroadcastProgram"
                required
                type="text"
                error={errors.program}
                placeholder={t("other:program-placeholder")}
                value={program}
                onBlur={() => {}}
                onChange={(e) => {
                  setProgram(e.target.value);
                }}
              />
              <Spacer />
              <Select
                id="txtBroadcastCountry"
                className="w-full text-old-sentric-dark-gray"
                name="country"
                error={errors.country}
                placeholder={t("other:select-country-placeholder")}
                value={country}
                onChange={(selected) => {
                  setCountry(selected);
                }}
                options={countries.map(option => ({
                  name: t(`countries:${option.name.replace(" ", "-")}`),
                  value: option.value,
                }))}
                search
                required
              />
              <Spacer />
              <Input
                id="dateBroadcastDate"
                required
                className={`${
                  isTunecore ? "tunecore-datepicker" : "themed-datepicker"
                } relative z-20`}
                type="date"
                dateFormatCalendar=" "
                showMonthDropdown
                showYearDropdown
                dateDropdownMode="select"
                useShortMonthInDropdown
                error={errors.dateAired}
                placeholder={t("other:date-aired-placeholder")}
                value={dateAired}
                onBlur={() => {}}
                onChange={(date) => {
                  setDateAired(date);
                }}
              />
              <Spacer />
              <TextArea
                name="txtBroadcastEvidence"
                required
                error={errors.evidence}
                placeholder={t("other:evidence-placeholder")}
                value={evidence}
                onBlur={() => {}}
                onChange={(e) => {
                  setEvidence(e.target.value);
                }}
              />
              <Spacer />
              <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
              <div className="flex justify-end w-full">
                <RebrandPrimaryButton
                  id={isUpdate ? "btnBroadcastEdit" : "btnBroadcastAdd"}
                  onClick={isUpdate ? updateBroadcast : addBroadcast}
                  disabled={pending}
                  pending={pending}
                >
                  {isUpdate
                    ? t("other:update-broadcast")
                    : t("other:add-broadcast")}
                </RebrandPrimaryButton>
              </div>
            </div>
          </div>
        )}
        {songs.isLoading && <LoadingSmall />}
      </div>
    </>
  );
}
export default BroadcastsCreateUpdate;
